/**
 * Icon Registry
 *
 * Central mapping of ontology types to icons.
 * - iconRegistry: type → icon string (MDI or $wob-* for custom SVG)
 * - alternativeIconRegistry: type → alternative icon string (e.g. alarm active state)
 *
 * WoB SVG icons are registered in Vuetify (src/plugins/vuetify.js) and referenced
 * with the $ prefix (e.g., '$wob-realestate'). Use these for equipment and points.
 *
 * Types not listed here will inherit from their parent class via the resolver.
 * Only add entries for types that need a specific icon different from their parent.
 */

// Legacy export for backwards compatibility (now empty, icons registered in Vuetify)
export const svgRegistry = {}

/**
 * MDI icon registry.
 * Maps lowercase type names to MDI icon strings.
 *
 * Icon inheritance: Types not listed here inherit from their parent class.
 * The hierarchy is: Collection/Space/Asset → System/Equipment → Point types
 */
export const iconRegistry = {
  // === Tree View Group ===
  'group': 'mdi-folder-outline',

  // === Superclass Fallbacks ===
  // These provide icons for entire branches of the ontology
  'collection': 'mdi-folder-multiple-outline',
  'space': 'mdi-cube-outline',
  'asset': 'mdi-package-variant',
  'system': 'mdi-sitemap',

  // === Point Type Fallbacks ===
  // All brick:Point subclasses inherit from these if no specific icon
  // Note: rec:Point is a geographic point (geopoint), brick:Point is a data capability
  'brick:point': 'mdi-chart-timeline-variant',
  'rec:point': 'mdi-map-marker',
  // Fallback for unqualified 'point' - uses brick:Point semantics (more common in this app)
  'point': 'mdi-chart-timeline-variant',
  'sensor': 'mdi-gauge',
  'setpoint': 'mdi-bullseye',
  'command': 'mdi-play-circle-outline',
  'status': 'mdi-checkbox-marked-circle-outline',
  'alarm': 'mdi-bell-alert-outline',
  'limit': 'mdi-arrow-collapse-vertical',

  // === System Types ===
  'air_system': 'mdi-air-filter',
  'ventilation_air_system': 'mdi-air-filter',
  'water_system': 'mdi-pipe',
  'hvac_system': 'mdi-hvac',
  'heating_ventilation_air_conditioning_system': 'mdi-hvac',
  'chilled_water_system': 'mdi-snowflake',
  'hot_water_system': 'mdi-water-thermometer',
  'domestic_hot_water_system': 'mdi-water-thermometer',
  'variable_air_volume_system': 'mdi-air-filter',

  // === Core Types ===
  'realestate': '$wob-realestate',
  'premises': 'mdi-home-group',
  'building': 'mdi-office-building',
  'subbuilding': 'mdi-cube-unfolded',
  'storey': 'mdi-stairs',
  'room': 'mdi-floor-plan',
  'device': 'mdi-cog-transfer',
  'equipment': 'mdi-nut',

  // === Building Elements ===
  'window': 'mdi-window-closed',
  'door': 'mdi-door-closed-lock',
  'roofouter': 'mdi-home-roof',
  'facade': 'mdi-wall',

  // === Spaces ===
  'entrance': 'mdi-mailbox',
  'hallway': 'mdi-rug',
  'garage': 'mdi-garage',
  'bicyclegarage': 'mdi-bicycle',
  'storage': 'mdi-archive-outline',

  // === Room Types ===
  'cookingroom': 'mdi-countertop-outline',
  'bathroom': 'mdi-shower',
  'livingroom': 'mdi-sofa',
  'bedroom': 'mdi-bed-king',
  'wardrobe': 'mdi-wardrobe',
  'cleaningcupboard': 'mdi-vacuum',
  'cleaningroom': 'mdi-vacuum',
  'telecommunicationroom': 'mdi-server-network',

  // === Apartments ===
  'lmapartment': 'mdi-floor-plan',
  'residentialapartment': 'mdi-floor-plan',

  // === Location/Geometry ===
  'georeferenceorigo': 'mdi-map-marker',
  'geometry': 'mdi-vector-square',
  'postaladdress': 'mdi-mail',
  // Note: brick:Point uses 'point' from superclass fallbacks (mdi-chart-timeline-variant)
  // rec:Point (geographic) would need a separate key if needed
  'entry': 'mdi-office-building-marker',
  'level': 'mdi-stairs-box',
  'architecturearea': 'mdi-set-square',
  'architecturecapacity': 'mdi-human-queue',

  // === Auth ===
  'authdomain': 'mdi-shield-key',
  'authprofile': 'mdi-account-key',

  // === Appliances ===
  'dishwasher': 'mdi-dishwasher',
  'combinedrefrigeratorfreezer': 'mdi-fridge',
  'washingmachine': 'mdi-washing-machine',

  // === Electrical ===
  'breaker_panel': 'mdi-fuse',
  'electrical_meter': 'mdi-counter',
  'battery': 'mdi-power-plug-battery-outline',

  // === Network Equipment ===
  'wirelessrouter': 'mdi-router-wireless',
  'patchport': 'mdi-ethernet',
  'ethernetswitch': 'mdi-switch',
  'ethernetport': 'mdi-ethernet',
  'patchpanel': 'mdi-router-network',
  'digitallock': 'mdi-lock-smart',

  // === Temperature Sensors ===
  'temperature_sensor': 'mdi-thermometer',
  'air_temperature_sensor': 'mdi-thermometer',
  'room_air_temperature_sensor': 'mdi-thermometer',
  'zone_air_temperature_sensor': 'mdi-thermometer',
  'outside_air_temperature_sensor': 'mdi-thermometer',
  'preheat_supply_air_temperature_sensor': 'mdi-thermometer',
  'intake_air_temperature_sensor': 'mdi-thermometer',
  'exhaust_air_temperature_sensor': 'mdi-thermometer',
  'discharge_air_temperature_sensor': 'mdi-thermometer',
  'supply_air_temperature_sensor': 'mdi-thermometer',
  'return_air_temperature_sensor': 'mdi-thermometer',
  // Water temperature sensors
  'water_temperature_sensor': 'mdi-thermometer-water',
  'leaving_hot_water_temperature_sensor': 'mdi-thermometer-water',
  'leaving_water_temperature_sensor': 'mdi-thermometer-water',
  'leaving_chilled_water_temperature_sensor': 'mdi-thermometer-water',
  'entering_water_temperature_sensor': 'mdi-thermometer-water',
  'entering_hot_water_temperature_sensor': 'mdi-thermometer-water',
  'entering_chilled_water_temperature_sensor': 'mdi-thermometer-water',
  'chilled_water_supply_temperature_sensor': 'mdi-thermometer-water',
  'chilled_water_return_temperature_sensor': 'mdi-thermometer-water',
  'hot_water_supply_temperature_sensor': 'mdi-thermometer-water',
  'hot_water_return_temperature_sensor': 'mdi-thermometer-water',
  'domestic_hot_water_temperature_sensor': 'mdi-thermometer-water',
  'domestic_hot_water_supply_temperature_sensor': 'mdi-thermometer-water',
  'domestic_hot_water_return_temperature_sensor': 'mdi-thermometer-water',

  // === Pressure Sensors ===
  'static_pressure_sensor': 'mdi-gauge',
  'air_static_pressure_sensor': 'mdi-gauge',
  'differential_pressure_sensor': 'mdi-gauge',
  'air_differential_pressure_sensor': 'mdi-gauge',
  'supply_air_static_pressure_sensor': 'mdi-gauge',
  'exhaust_air_static_pressure_sensor': 'mdi-gauge',
  'exhaust_air_differential_pressure_sensor': 'mdi-gauge',
  'supply_air_differential_pressure_sensor': 'mdi-gauge',
  'return_air_static_pressure_sensor': 'mdi-gauge',
  'filter_air_differential_pressure_sensor': 'mdi-gauge',
  'water_pressure_sensor': 'mdi-gauge',
  'chilled_water_differential_pressure_sensor': 'mdi-gauge',
  'hot_water_differential_pressure_sensor': 'mdi-gauge',

  // === Flow Sensors ===
  'flow_sensor': 'mdi-waves-arrow-right',
  'air_flow_sensor': 'mdi-weather-windy',
  'supply_air_flow_sensor': 'mdi-weather-windy',
  'exhaust_air_flow_sensor': 'mdi-weather-windy',
  'return_air_flow_sensor': 'mdi-weather-windy',
  'water_flow_sensor': 'mdi-waves-arrow-right',
  'chilled_water_flow_sensor': 'mdi-waves-arrow-right',
  'chilled_water_supply_flow_sensor': 'mdi-waves-arrow-right',
  'chilled_water_return_flow_sensor': 'mdi-waves-arrow-right',
  'chilled_water_discharge_flow_sensor': 'mdi-waves-arrow-right',
  'hot_water_flow_sensor': 'mdi-waves-arrow-right',
  'hot_water_return_flow_sensor': 'mdi-waves-arrow-right',
  'water_volume_sensor': 'mdi-waves-arrow-right',

  // === Humidity Sensors ===
  'humidity_sensor': 'mdi-water-percent',
  'relative_humidity_sensor': 'mdi-water-percent',
  'exhaust_air_humidity_sensor': 'mdi-water-percent',
  'supply_air_humidity_sensor': 'mdi-water-percent',
  'return_air_humidity_sensor': 'mdi-water-percent',
  'outside_air_humidity_sensor': 'mdi-water-percent',
  'zone_air_humidity_sensor': 'mdi-water-percent',

  // === Air Quality Sensors ===
  'co2_sensor': 'mdi-molecule-co2',
  'return_air_co2_sensor': 'mdi-molecule-co2',
  'zone_co2_level_sensor': 'mdi-molecule-co2',
  'return_air_co_sensor': 'mdi-molecule-co',
  'zone_co_level_sensor': 'mdi-molecule-co',
  'iaqsensorequipment': 'mdi-weather-windy',

  // === Other Sensors ===
  'energy_sensor': 'mdi-lightning-bolt',
  'energy_usage_sensor': 'mdi-lightning-bolt',
  'power_sensor': 'mdi-flash',
  'battery_voltage_sensor': 'mdi-battery-charging',
  'water_usage_sensor': 'mdi-water',
  'occupancy_sensor': 'mdi-motion-sensor',
  'motion_sensor': 'mdi-motion-sensor',
  'on_timer_sensor': 'mdi-timer-outline',

  // === HVAC Equipment ===
  'ahu': 'mdi-hvac',
  'air_handler_unit': 'mdi-hvac',
  'air_handling_unit': 'mdi-hvac',
  'heat_wheel': '$wob-heatwheel',
  'heat_exchanger': '$wob-heatwheel',
  'variable_air_volume_box': 'mdi-air-filter',
  'variable_air_volume_cooling_beam': 'mdi-air-filter',

  // === Fans ===
  'fan': 'mdi-fan',
  'exhaust_fan': 'mdi-fan',
  'supply_fan': 'mdi-fan',
  'transfer_fan': 'mdi-fan',

  // === Pumps ===
  'pump': 'mdi-pump',
  'water_pump': 'mdi-pump',
  'chilled_water_pump': 'mdi-pump',
  'hot_water_pump': 'mdi-pump',
  'hot_water_circulator_pump': 'mdi-pump',
  'circulator_pump': 'mdi-pump',
  'domestic_hot_water_circulator_pump': 'mdi-pump',

  // === Valves ===
  'valve': 'mdi-pipe-valve',
  'water_valve': 'mdi-pipe-valve',
  'chilled_water_valve': 'mdi-pipe-valve',
  'hot_water_valve': 'mdi-pipe-valve',
  'domestic_hot_water_valve': 'mdi-pipe-valve',
  'preheat_hot_water_valve': 'mdi-pipe-valve',

  // === Dampers ===
  'damper': 'mdi-valve',
  'bypass_damper': 'mdi-valve',
  'exhaust_damper': 'mdi-valve',
  'outside_damper': 'mdi-valve',
  'return_damper': 'mdi-valve',
  'mixed_damper': 'mdi-valve',
  'zone_damper': 'mdi-valve',

  // === Meters ===
  'meter': 'mdi-counter',
  'building_water_meter': 'mdi-counter',
  'building_chilled_water_meter': 'mdi-counter',
  'building_hot_water_meter': 'mdi-counter',
  'chilled_water_meter': 'mdi-counter',
  'hot_water_meter': 'mdi-counter',

  // === Controllers ===
  'controller': 'mdi-chip',
  'sensor_equipment': 'mdi-access-point',

  // === Temperature Setpoints ===
  'temperature_setpoint': 'mdi-thermostat',
  'air_temperature_setpoint': 'mdi-thermostat',
  'supply_air_temperature_setpoint': 'mdi-thermostat',
  'return_air_temperature_setpoint': 'mdi-thermostat',
  'zone_air_temperature_setpoint': 'mdi-thermostat',
  'effective_air_temperature_setpoint': 'mdi-thermostat',
  'effective_supply_air_temperature_setpoint': 'mdi-thermostat',
  'effective_return_air_temperature_setpoint': 'mdi-thermostat',
  'water_temperature_setpoint': 'mdi-thermostat',
  'domestic_hot_water_temperature_setpoint': 'mdi-thermostat',
  'domestic_hot_water_supply_temperature_setpoint': 'mdi-thermostat',
  'entering_chilled_water_temperature_setpoint': 'mdi-thermostat',
  'leaving_chilled_water_temperature_setpoint': 'mdi-thermostat',
  'return_chilled_water_temperature_setpoint': 'mdi-thermostat',
  'supply_chilled_water_temperature_setpoint': 'mdi-thermostat',
  'return_hot_water_temperature_setpoint': 'mdi-thermostat',
  'supply_hot_water_temperature_setpoint': 'mdi-thermostat',
  'min_water_temperature_setpoint': 'mdi-thermostat',
  'target_setpoint': 'mdi-bullseye-arrow',

  // === Pressure Setpoints ===
  'pressure_setpoint': 'mdi-gauge-full',
  'static_pressure_setpoint': 'mdi-gauge-full',
  'air_differential_pressure_setpoint': 'mdi-gauge-full',
  'supply_air_static_pressure_setpoint': 'mdi-gauge-full',
  'exhaust_air_static_pressure_setpoint': 'mdi-gauge-full',
  'return_air_static_pressure_setpoint': 'mdi-gauge-full',
  'water_pressure_setpoint': 'mdi-gauge-full',
  'chilled_water_differential_pressure_setpoint': 'mdi-gauge-full',
  'hot_water_differential_pressure_setpoint': 'mdi-gauge-full',

  // === Flow Setpoints ===
  'flow_setpoint': 'mdi-waves-arrow-right',
  'air_flow_setpoint': 'mdi-weather-windy',
  'supply_air_flow_setpoint': 'mdi-weather-windy',
  'exhaust_air_flow_setpoint': 'mdi-weather-windy',
  'return_air_flow_setpoint': 'mdi-weather-windy',

  // === CO2 Setpoints ===
  'zone_co2_level_setpoint': 'mdi-molecule-co2',
  'zone_co_level_setpoint': 'mdi-molecule-co',

  // === Limits ===
  'max_limit': 'mdi-arrow-collapse-up',
  'min_limit': 'mdi-arrow-collapse-down',
  'max_supply_air_static_pressure_setpoint_limit': 'mdi-arrow-collapse-up',
  'min_supply_air_static_pressure_setpoint_limit': 'mdi-arrow-collapse-down',
  'max_return_air_static_pressure_setpoint_limit': 'mdi-arrow-collapse-up',
  'min_return_air_static_pressure_setpoint_limit': 'mdi-arrow-collapse-down',
  'max_supply_air_temperature_setpoint_limit': 'mdi-arrow-collapse-up',
  'min_supply_air_temperature_setpoint_limit': 'mdi-arrow-collapse-down',

  // === Commands ===
  'start_stop_command': '$wob-fan-startstop',
  'fan_speed_command': '$wob-fan-speed',
  'fan_start_stop_command': '$wob-fan-startstop',
  'preheat_command': 'mdi-thermometer-plus',
  'heating_command': 'mdi-thermometer-plus',
  'cooling_command': 'mdi-thermometer-minus',
  'pump_command': '$wob-pump-speed',
  'pump_speed_command': '$wob-pump-speed',
  'pump_start_stop_command': '$wob-pump-startstop',
  'valve_command': '$wob-valve-openclose',
  'valve_position_command': '$wob-valve-position',
  'damper_command': '$wob-damper-openclose',
  'damper_position_command': '$wob-damper-position',
  'heatwheel_speed_command': '$wob-heatwheel-speed',
  'heat_wheel_speed_command': '$wob-heatwheel-speed',
  'heatwheel_start_stop_command': '$wob-heatwheel-startstop',
  'heat_wheel_start_stop_command': '$wob-heatwheel-startstop',
  'onetimepasswordcredentialscommand': 'mdi-state-machine',
  'physicaldigitalkeycredentialscommand': 'mdi-state-machine',

  // === Status ===
  'on_off_status': 'mdi-toggle-switch-outline',
  'fan_on_off_status': '$wob-fan-status',
  'fan_status': '$wob-fan-status',
  'pump_on_off_status': '$wob-pump-status',
  'pump_status': '$wob-pump-status',
  'valve_status': '$wob-valve-status',
  'valve_on_off_status': '$wob-valve-status',
  'damper_status': '$wob-damper-status',
  'heatwheel_status': '$wob-heatwheel-status',
  'heat_wheel_status': '$wob-heatwheel-status',
  'heatwheel_on_off_status': '$wob-heatwheel-status',
  'heat_wheel_on_off_status': '$wob-heatwheel-status',
  'availability_status': 'mdi-check-circle-outline',
  'heating_operation_status': 'mdi-fire',

  // === Alarms ===
  'freeze_guard_alarm': 'mdi-snowflake-alert',
  'high_temperature_alarm': 'mdi-thermometer-alert',
  'low_temperature_alarm': 'mdi-thermometer-alert',
  'leaving_water_temperature_alarm': 'mdi-thermometer-alert',
  'low_static_pressure_alarm': 'mdi-gauge-empty',
  'power_alarm': 'mdi-flash-alert',
  'cycle_alarm': 'mdi-sync-alert',
  'outside_officehours_alarm': 'mdi-clock-alert-outline',
  'power_loss_alarm': 'mdi-power-plug-outline',
  'communication_loss_alarm': 'mdi-network-outline',
  'low_battery_alarm': 'mdi-battery-outline',
  'exceptionevent': 'mdi-alert-outline',
  // Equipment-specific alarms
  'damper_alarm': '$wob-damper-alarm',
  'fan_alarm': 'mdi-fan-alert',
  'heatwheel_alarm': '$wob-heatwheel-alarm',
  'heat_wheel_alarm': '$wob-heatwheel-alarm',
  'pump_alarm': '$wob-pump-alarm',
  'valve_alarm': '$wob-valve-alarm',

  // === Observations/Events ===
  'observation': 'mdi-chart-timeline-variant',

  // === Parameters ===
  'parameter': 'mdi-cog',
}

/**
 * Alternative MDI icons (e.g. alarm active states).
 * Used for visual distinction when alarm is triggered.
 */
export const alternativeIconRegistry = {
  // Active/triggered alarm states
  alarm: { mdi: 'mdi-bell-ring' },
  freeze_guard_alarm: { mdi: 'mdi-snowflake-alert' },
  high_temperature_alarm: { mdi: 'mdi-thermometer-high' },
  low_temperature_alarm: { mdi: 'mdi-thermometer-low' },
  power_loss_alarm: { mdi: 'mdi-power-plug-off-outline' },
  communication_loss_alarm: { mdi: 'mdi-network-off-outline' },
  low_battery_alarm: { mdi: 'mdi-battery-off-outline' },
  power_alarm: { mdi: 'mdi-flash-off' },
}
